// Supabase client for the admin SPA. Used purely for auth — sign-in via
// Google OAuth, session management, JWT retrieval. The bot trusts the
// JWTs Supabase issues (verifies signature with SUPABASE_JWT_SECRET).
//
// VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are public values from
// Supabase dashboard → Settings → API. The anon key is safe to ship in
// the SPA bundle; it can only do what RLS policies allow.
import { createClient, type Session } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !anonKey) {
  // Surface a loud, actionable error in dev. In a real prod build the
  // VITE_* vars are baked at build time so this branch never fires there.
  console.error('[supabase] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY missing — set them in bot/frontend/.env.local');
}

export const supabase = createClient(url ?? 'http://localhost', anonKey ?? 'anon', {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

export type { Session };

export async function signInWithPassword(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}
